import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface Batch {
    id: string;
    inputId: string;
    batchNumber: string;
    manufacturerBatchNumber: string;
    lotNumber: string;
    receivedQuantity: number;
    receivedDate: string;
    manufactureDate: string;
    expiryDate: string;
    unitCost: number;
    purchaseOrderNumber: string;
    invoiceNumber: string;
    createdAt: string;
}

export interface CreateBatchRequest {
    inputId: string;
    supplierId?: string;
    batchNumber?: string;
    manufacturerBatchNumber: string;
    lotNumber: string;
    receivedQuantity: number;
    receivedDate: string;
    manufactureDate: string;
    expiryDate?: string | null;
    unitCost: number;
    commercialQuantity?: number | null;
    commercialUnit?: string | null;
    commercialUnitCost?: number | null;
    purchaseOrderNumber: string;
    invoiceNumber: string;
    numberOfPacks?: number | null;
    packSize?: number | null;
    seedCount?: number | null;  // Total seeds on packet label (seed inputs only)
}

// Reference number + approval toggle removed in PR 3 of stock-inputs overhaul
// (SPEC §d/§e). The backend ignores any leftover `referenceNumber`/`requiresApproval`
// payload, but emit a minimal payload so the wire stays clean.
export interface CreateReceiptRequest {
    inputId: string;
    inputBatchId: string;
    quantity: number;
    movementDate: string;
    notes: string;
    unit: string;
}

export interface StockReceipt {
    id: string;
    inputId: string;
    inputBatchId: string;
    quantity: number;
    movementDate: string;
    notes?: string;
    unit: string;
}

export const stockReceiptApi = {
    createBatch: async (payload: CreateBatchRequest): Promise<Batch> => {
        const { data } = await apiClient.post<ApiResponse<Batch>>(
            "/Stock/batches",
            payload
        );
        return data.data;
    },

    createBulkBatches: async (
        payloads: CreateBatchRequest[]
    ): Promise<Batch[]> => {
        const promises = payloads.map((payload) =>
            stockReceiptApi.createBatch(payload)
        );
        return Promise.all(promises);
    },

    createReceipt: async (payload: CreateReceiptRequest): Promise<StockReceipt> => {
        const { data } = await apiClient.post<ApiResponse<StockReceipt>>(
            "/Stock/receipts",
            payload
        );
        return data.data;
    },

    createBatchesWithReceipts: async (
        lines: { batch: CreateBatchRequest; unit: string }[],
        receiptData: Omit<CreateReceiptRequest, "inputId" | "inputBatchId" | "quantity" | "unit">
    ): Promise<StockReceipt[]> => {
        const createdBatches = await Promise.all(
            lines.map((line) => stockReceiptApi.createBatch(line.batch))
        );

        const receipts: StockReceipt[] = [];
        for (const [index, batch] of createdBatches.entries()) {
            // A first receipt may establish this input's native unit. Complete it
            // before posting another line for the same input.
            receipts.push(await stockReceiptApi.createReceipt({
                inputId: batch.inputId,
                inputBatchId: batch.id,
                quantity: batch.receivedQuantity,
                ...receiptData,
                unit: lines[index].unit,
            }));
        }
        return receipts;
    },
};
